import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassIcon,
  Lock as LockIcon
} from '@mui/icons-material';

import { getLocalizedField } from '../utils/courseDisplay';

type Translation = { [k: string]: string } | null | undefined;

const formatDate = (v?: string | null): string => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString('fr-FR');
  } catch {
    return v;
  }
};

interface AssessmentMeasurement {
  measurement: 'initial' | 'final' | 'followup';
  followupId?: string;
  label?: Translation;
  status: 'submitted' | 'available' | 'locked';
  submittedAt?: string | null;
  unlockDate?: string | null;
  blockedReason?: 'workshops' | 'chapters' | 'feature-disabled' | null;
  expectedAfter?: string | null;
  answers?: Array<{
    questionText?: Translation;
    questionType: 'weighted-mcq' | 'scale' | 'open-text';
    isLinked: boolean;
    linkedKey?: string;
    selectedOptionText?: Translation;
    selectedOptionIndex?: number;
    pointsScored?: number;
    scaleValue?: number;
    textValue?: string;
  }>;
}

interface LinkedEvolutionRow {
  linkedKey: string;
  type: 'weighted-mcq' | 'scale';
  question: Translation;
  scale?: { min: number; max: number; step?: number };
  optionsCount?: number;
  maxPoints?: number;
  points: Array<{
    measurement: 'initial' | 'final' | 'followup';
    followupId?: string;
    label?: Translation;
    submittedAt: string;
    value: number | null;
    type: 'weighted-mcq' | 'scale';
  }>;
}

export interface StudentAssessmentSectionProps {
  enabled: boolean;
  measurements: AssessmentMeasurement[];
  linkedEvolution: LinkedEvolutionRow[];
  defaultLanguage?: string;
}

const statusChip = (m: AssessmentMeasurement) => {
  if (m.status === 'submitted') {
    return (
      <Chip
        size="small"
        color="success"
        icon={<CheckCircleIcon fontSize="small" />}
        label={`Soumis le ${formatDate(m.submittedAt || undefined)}`}
      />
    );
  }
  if (m.status === 'available') {
    return (
      <Chip
        size="small"
        color="warning"
        icon={<HourglassIcon fontSize="small" />}
        label="Disponible — non soumis"
      />
    );
  }
  let txt = 'Verrouillé';
  if (m.blockedReason === 'workshops')
    txt = `Disponible après ${formatDate(m.expectedAfter || undefined)}`;
  else if (m.blockedReason === 'chapters') txt = 'En attente de fin des sections';
  else if (m.unlockDate) txt = `Disponible à partir du ${formatDate(m.unlockDate)}`;
  return <Chip size="small" icon={<LockIcon fontSize="small" />} label={txt} />;
};

const StudentAssessmentSection: React.FC<StudentAssessmentSectionProps> = ({
  enabled,
  measurements,
  linkedEvolution,
  defaultLanguage = 'en'
}) => {
  const localize = (t: Translation, fallback = '-') =>
    getLocalizedField(t, defaultLanguage, fallback);

  const measurementTitle = (m: AssessmentMeasurement) => {
    if (m.measurement === 'initial') return 'Diagnostic initial';
    if (m.measurement === 'final') return 'Mesure d\'impact finale';
    return localize(m.label, 'Mesure de suivi');
  };

  const renderAnswer = (a: NonNullable<AssessmentMeasurement['answers']>[number]) => {
    if (a.questionType === 'weighted-mcq') {
      return `${localize(a.selectedOptionText)} (${a.pointsScored ?? 0} pts)`;
    }
    if (a.questionType === 'scale') {
      return `${a.scaleValue ?? '—'}`;
    }
    if (a.questionType === 'open-text') {
      return a.textValue || '—';
    }
    return '—';
  };

  if (!enabled) {
    return null;
  }

  const submittedCount = measurements.filter((m) => m.status === 'submitted').length;

  return (
    <Accordion sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box width="100%" display="flex" justifyContent="space-between" alignItems="center" gap={2} pr={1}>
          <Typography variant="h6" fontWeight={700}>
            Diagnostic & Mesures d'impact
          </Typography>
          <Chip
            size="small"
            label={`${submittedCount}/${measurements.length} soumis`}
            color={submittedCount === measurements.length && measurements.length > 0 ? 'success' : 'default'}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {/* Tableau d'évolution sur les questions liées */}
        {linkedEvolution.length > 0 && (
          <Box sx={{ mt: 1, mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Évolution sur les questions liées
            </Typography>
            <TableContainer
              component={Box}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, width: '40%' }}>Question</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Diagnostic</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Mesure finale</TableCell>
                    {/* Suivis : en colonnes dynamiques (jusqu'aux N suivis) */}
                    {linkedEvolution[0]?.points
                      .filter((p) => p.measurement === 'followup')
                      .map((p, idx) => (
                        <TableCell key={`hdr-fu-${idx}`} sx={{ fontWeight: 700 }}>
                          {localize(p.label, `Suivi ${idx + 1}`)}
                        </TableCell>
                      ))}
                    <TableCell sx={{ fontWeight: 700 }}>Évolution</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {linkedEvolution.map((row) => {
                    const initial = row.points.find((p) => p.measurement === 'initial');
                    const final = row.points.find((p) => p.measurement === 'final');
                    const followups = row.points.filter((p) => p.measurement === 'followup');
                    const last =
                      [...row.points].sort(
                        (a, b) =>
                          new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime()
                      )[row.points.length - 1] || null;
                    const delta =
                      initial?.value != null && last?.value != null
                        ? last.value - initial.value
                        : null;
                    return (
                      <TableRow key={row.linkedKey} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {localize(row.question)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.type === 'weighted-mcq'
                              ? `QCM pondéré (max ${row.maxPoints ?? '—'} pts)`
                              : `Échelle ${row.scale?.min ?? '—'}→${row.scale?.max ?? '—'}`}
                          </Typography>
                        </TableCell>
                        <TableCell>{initial?.value ?? '—'}</TableCell>
                        <TableCell>{final?.value ?? '—'}</TableCell>
                        {followups.map((p, idx) => (
                          <TableCell key={`row-fu-${idx}`}>{p.value ?? '—'}</TableCell>
                        ))}
                        <TableCell>
                          {delta == null ? (
                            '—'
                          ) : (
                            <Chip
                              size="small"
                              color={delta > 0 ? 'success' : delta < 0 ? 'error' : 'default'}
                              label={`${delta > 0 ? '+' : ''}${delta}`}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Détail par mesure */}
        <Stack spacing={1.5}>
          {measurements.map((m) => {
            const id = `${m.measurement}-${m.followupId ?? ''}`;
            return (
              <Accordion
                key={id}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '8px !important',
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" width="100%">
                    <Typography variant="subtitle1" fontWeight={700}>
                      {measurementTitle(m)}
                    </Typography>
                    <Box ml="auto">{statusChip(m)}</Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {!m.answers || m.answers.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Aucune réponse enregistrée.
                    </Typography>
                  ) : (
                    <TableContainer
                      component={Box}
                      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, width: '50%' }}>Question</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Réponse</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {m.answers.map((a, idx) => (
                            <TableRow key={`${id}-a-${idx}`}>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {localize(a.questionText)}
                                </Typography>
                                {a.isLinked && (
                                  <Chip
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    label="Liée"
                                    sx={{ mt: 0.5 }}
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                {a.questionType === 'weighted-mcq'
                                  ? 'QCM pondéré'
                                  : a.questionType === 'scale'
                                  ? 'Échelle'
                                  : 'Texte libre'}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                                {renderAnswer(a)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default StudentAssessmentSection;
